import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { BOLD_SX, SECONDARY_TEXT_SX, TRUNCATE_SX } from '../styles';
import { valueColor } from '../utils';
import type { EnrichedHolding } from '../utils/portfolioRevampBuilders';

const BoldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant='labelSmall' sx={BOLD_SX}>
    {children}
  </Typography>
);

const HoldingCell = ({
  w,
  n,
  children,
  sx,
}: {
  w: string;
  n?: boolean;
  children: React.ReactNode;
  sx?: object;
}) => (
  <TableCell number={n} sx={{ width: w, padding: '4px 16px', ...sx }}>
    {children}
  </TableCell>
);

export type PortfolioRowDetailPositionsRowProps = {
  holding: EnrichedHolding;
  theme: Theme;
};

export const PortfolioRowDetailPositionsRow = ({
  holding,
  theme,
}: PortfolioRowDetailPositionsRowProps) => {
  const { t } = useTranslation('portfolioTradingHub');
  const isCash = holding.financialProduct?.isCash;
  const isDrift = Math.abs(holding.driftPct) > 0.5;
  const currency =
    holding.currency || holding.financialProduct?.currency || '—';

  return (
    <TableRow
      sx={{
        bgcolor: isDrift ? theme.color.warningVariant : 'transparent',
        '&:hover': { bgcolor: theme.color.background },
      }}
    >
      <HoldingCell w='18%'>
        {isCash ? (
          <BoldLabel>{t('rowDetail.generalInfo.cash')}</BoldLabel>
        ) : (
          <Box>
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {holding.financialProduct?.ticker}
            </Typography>
            <Typography
              variant='bodySmall'
              sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
            >
              {holding.financialProduct?.name}
            </Typography>
          </Box>
        )}
      </HoldingCell>
      <HoldingCell w='7%'>{currency}</HoldingCell>
      <HoldingCell w='11%' n>
        {isCash ? (
          '-'
        ) : (
          <>
            {holding.quantity?.toFixed(2)}
            {holding.pendingQuantity !== 0 && (
              <Typography
                variant='labelSmall'
                component='span'
                sx={{ color: theme.color.warning, display: 'block' }}
              >
                {holding.pendingQuantity > 0 ? '+' : ''}
                {holding.pendingQuantity?.toFixed(2)}
              </Typography>
            )}
          </>
        )}
      </HoldingCell>
      <HoldingCell w='11%' n>
        {isCash
          ? '-'
          : formatMoneyValue(
              holding.financialProduct?.currentPriceCents,
              '$',
              2,
            )}
      </HoldingCell>
      <HoldingCell w='12%' n sx={{ fontWeight: 600 }}>
        {formatMoneyValue(holding.valueCents, '$', 2)}
      </HoldingCell>
      <HoldingCell w='9%' n sx={{ fontWeight: 600 }}>
        {holding.currentPct.toFixed(1)}%
      </HoldingCell>
      <HoldingCell w='9%' n>
        {holding.targetPct.toFixed(1)}%
      </HoldingCell>
      <HoldingCell
        w='10%'
        n
        sx={{
          fontWeight: isDrift ? 700 : 400,
          color: isDrift
            ? valueColor(theme.color, holding.driftPct)
            : theme.color.onSurface,
        }}
      >
        {holding.driftPct > 0 ? '+' : ''}
        {holding.driftPct.toFixed(1)}%
      </HoldingCell>
      <HoldingCell
        w='13%'
        n
        sx={{
          fontWeight: isDrift ? 700 : 400,
          color: isDrift
            ? valueColor(theme.color, holding.driftCents)
            : theme.color.onSurface,
        }}
      >
        {holding.driftCents > 0 ? '+' : ''}
        {formatMoneyValue(holding.driftCents, '$', 2)}
      </HoldingCell>
    </TableRow>
  );
};
